import React from "react";
import { Modal, Skeleton, Image, Carousel } from "antd";
import { Place } from "../PublicPlacesStore";
import StatusBadge from "./StatusBadge.tsx";

interface PlaceDetailModalProps {
    open: boolean;
    onCancel: () => void;
    isLoading: boolean;
    selectedPlace: Place | null;
    placeDetail: any;
}

const PlaceDetailModal: React.FC<PlaceDetailModalProps> = ({
    open,
    onCancel,
    isLoading,
    selectedPlace,
    placeDetail,
}) => {
    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={720}
            title={
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-primary-100 flex items-center justify-center text-primary-700">
                        <i className="fa-solid fa-location-dot"></i>
                    </div>
                    <div>
                        <div className="text-lg font-bold text-secondary-900">{selectedPlace?.name}</div>
                        {selectedPlace && <StatusBadge status={selectedPlace.status} />}
                    </div>
                </div>
            }
        >
            {isLoading ? (
                <div className="py-8">
                    <Skeleton active paragraph={{ rows: 6 }} />
                </div>
            ) : selectedPlace ? (
                <div className="space-y-6">
                    <div className="w-full aspect-square rounded-2xl overflow-hidden bg-gray-100">
                        {placeDetail?.media && placeDetail.media.length > 1 ? (
                            <Carousel autoplay className="w-full h-full">
                                {placeDetail.media.map((item: any, index: number) => (
                                    <div key={item.id || index} className="w-full h-full">
                                        <Image
                                            src={item.url}
                                            alt={`${selectedPlace.name} - ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            rootClassName="w-full h-full"
                                            style={{ objectFit: "cover", width: "100%", height: "100%" }}
                                        />
                                    </div>
                                ))}
                            </Carousel>
                        ) : placeDetail?.media && placeDetail.media.length === 1 ? (
                            <Image
                                src={placeDetail.media[0].url}
                                alt={selectedPlace.name}
                                className="w-full h-full object-cover"
                                rootClassName="w-full h-full"
                                style={{ objectFit: "cover", width: "100%", height: "100%" }}
                            />
                        ) : selectedPlace.image ? (
                            <Image
                                src={selectedPlace.image}
                                alt={selectedPlace.name}
                                className="w-full h-full object-cover"
                                rootClassName="w-full h-full"
                                style={{ objectFit: "cover", width: "100%", height: "100%" }}
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-yellow-100 to-yellow-200 flex items-center justify-center">
                                <i className="fa-solid fa-image text-primary-600 text-4xl"></i>
                            </div>
                        )}
                    </div>

                    {/* Basic Info */}
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <i className="fa-solid fa-location-dot text-primary-400 mt-1"></i>
                            <div className="flex-1">
                                <p className="text-secondary-700 font-medium">{placeDetail?.address || selectedPlace.address}</p>
                                {placeDetail?.latitude && placeDetail?.longitude && (
                                    <p className="text-xs text-secondary-500 mt-1">
                                        {placeDetail.latitude}, {placeDetail.longitude}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Owner Info */}
                        {placeDetail?.user && (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                <Image
                                    src={placeDetail.user.avatar}
                                    alt={placeDetail.user.name}
                                    width={40}
                                    height={40}
                                    className="rounded-full object-cover"
                                    preview={false}
                                />
                                <div>
                                    <p className="text-xs text-secondary-500">Người đăng</p>
                                    <p className="text-sm font-semibold text-secondary-900">{placeDetail.user.name}</p>
                                </div>
                            </div>
                        )}

                        {placeDetail?.description && (
                            <div>
                                <h4 className="text-sm font-semibold text-secondary-900 mb-2">Mô tả</h4>
                                <p className="text-secondary-700 leading-relaxed">{placeDetail.description}</p>
                            </div>
                        )}

                        {/* Rating & Reviews */}
                        {(placeDetail?.rating || selectedPlace.rating) && (
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-star text-primary-400"></i>
                                    <span className="font-semibold text-secondary-900">
                                        {(placeDetail?.rating || selectedPlace.rating)?.toFixed(1)}
                                    </span>
                                </div>
                                {placeDetail?.reviews_count !== undefined && (
                                    <span className="text-sm text-secondary-500">
                                        ({placeDetail.reviews_count} đánh giá)
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Category & Activity Groups */}
                        <div className="flex flex-wrap gap-2">
                            {placeDetail?.categories && placeDetail.categories.map((cat: any) => (
                                <span key={cat.id} className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                                    {cat.display_name}
                                </span>
                            ))}
                            {/* Fallback for old structure if needed */}
                            {placeDetail?.category && !placeDetail?.categories && (
                                <span className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                                    {placeDetail.category}
                                </span>
                            )}
                            {placeDetail?.activity_groups && placeDetail.activity_groups.length > 0 && (
                                <>
                                    {placeDetail.activity_groups.map((g: any) => (
                                        <span
                                            key={g.id}
                                            className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold"
                                        >
                                            {g.name}
                                        </span>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Price */}
                        {placeDetail?.average_price && (
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-money-bill-wave text-primary-400"></i>
                                <span className="text-secondary-700">
                                    Giá trung bình: {placeDetail.average_price.toLocaleString('vi-VN')} VNĐ
                                </span>
                            </div>
                        )}

                        {/* Contact Info */}
                        {(placeDetail?.contact_phone || placeDetail?.contact_email) && (
                            <div className="pt-4 border-t border-secondary-100 space-y-2">
                                <h4 className="text-sm font-semibold text-secondary-900">Thông tin liên hệ</h4>
                                {placeDetail.contact_phone && (
                                    <div className="flex items-center gap-2 text-secondary-700">
                                        <i className="fa-solid fa-phone text-primary-400"></i>
                                        <span>{placeDetail.contact_phone}</span>
                                    </div>
                                )}
                                {placeDetail.contact_email && (
                                    <div className="flex items-center gap-2 text-secondary-700">
                                        <i className="fa-solid fa-envelope text-primary-400"></i>
                                        <span>{placeDetail.contact_email}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tags */}
                        {placeDetail?.tags && placeDetail.tags.length > 0 && (
                            <div className="pt-4 border-t border-secondary-100">
                                <h4 className="text-sm font-semibold text-secondary-900 mb-2">Tags</h4>
                                <div className="flex flex-wrap gap-2">
                                    {placeDetail.tags.map((tag: string, index: number) => (
                                        <span
                                            key={index}
                                            className="px-2 py-1 rounded-lg bg-secondary-50 text-secondary-600 text-xs"
                                        >
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Reviews */}
                        {placeDetail?.reviews && placeDetail.reviews.length > 0 && (
                            <div className="pt-4 border-t border-secondary-100">
                                <h4 className="text-sm font-semibold text-secondary-900 mb-3">
                                    Đánh giá gần đây ({placeDetail.reviews.length})
                                </h4>
                                <div className="space-y-4">
                                    {placeDetail.reviews.map((review: any) => (
                                        <div key={review.id} className="flex gap-3">
                                            <Image
                                                src={review.user?.avatar}
                                                alt={review.user?.name}
                                                width={32}
                                                height={32}
                                                className="rounded-full object-cover"
                                                preview={false}
                                                fallback="https://ui-avatars.com/api/?name=User"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium text-secondary-900">
                                                        {review.user?.name || "Người dùng ẩn danh"}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-xs font-semibold">{review.rating}</span>
                                                        <i className="fa-solid fa-star text-yellow-400 text-[10px]"></i>
                                                    </div>
                                                </div>
                                                {review.comment && (
                                                    <p className="text-sm text-secondary-600 bg-gray-50 p-2 rounded-lg">
                                                        {review.comment}
                                                    </p>
                                                )}
                                                <p className="text-[10px] text-secondary-400 mt-1">
                                                    {new Date(review.created_at).toLocaleDateString('vi-VN')}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};

export default PlaceDetailModal;
